(() => {
  const ROOT_SELECTOR = '.zayn-proof-gallery';
  const COUNTER_SELECTOR = '.zayn-proof-gallery__metrics strong';
  const DURATION = 1400;

  const formatNumber = (value) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en').format(value);
    } catch (_) {
      return String(value);
    }
  };

  const parseTarget = (text) => {
    const normalized = String(text || '').replace(/[^0-9.-]/g, '');
    const value = Number(normalized);
    return Number.isFinite(value) && value >= 0 ? value : null;
  };

  const animateCounter = (element, target, finalText) => {
    const startedAt = performance.now();
    element.textContent = '0';

    const frame = (now) => {
      const progress = Math.min((now - startedAt) / DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      element.textContent = formatNumber(Math.round(target * eased));

      if (progress < 1) {
        element._zaynCounterRaf = requestAnimationFrame(frame);
      } else {
        element.textContent = finalText;
        element.removeAttribute('aria-busy');
      }
    };

    element.setAttribute('aria-busy', 'true');
    element._zaynCounterRaf = requestAnimationFrame(frame);
  };

  const startCounters = (root) => {
    if (!root || root.dataset.zaynDedicatedCounterDone === 'true') return;

    const counters = Array.from(root.querySelectorAll(COUNTER_SELECTOR));
    if (!counters.length) return;

    root.dataset.zaynDedicatedCounterDone = 'true';

    counters.forEach((element) => {
      const finalText = element.getAttribute('aria-label') || element.dataset.zaynCounterTarget || element.textContent.trim();
      const target = parseTarget(finalText);
      if (target === null) return;

      element.dataset.zaynCounterTarget = finalText;

      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        element.textContent = finalText;
        return;
      }

      if (element._zaynCounterRaf) cancelAnimationFrame(element._zaynCounterRaf);
      animateCounter(element, target, finalText);
    });
  };

  const observeRoot = (root) => {
    if (!root || root.dataset.zaynDedicatedCounterBound === 'true') return;

    if (root._zaynProofObserver) {
      root._zaynProofObserver.disconnect();
      root._zaynProofObserver = null;
    }
    root.dataset.zaynProofCounterInit = 'true';
    root.dataset.zaynProofCounted = 'true';
    root.dataset.zaynDedicatedCounterBound = 'true';

    if (!('IntersectionObserver' in window)) {
      startCounters(root);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          startCounters(root);
          observer.disconnect();
          root._zaynDedicatedCounterObserver = null;
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(root);
    root._zaynDedicatedCounterObserver = observer;
  };

  const init = (scope = document) => {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) observeRoot(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll(ROOT_SELECTOR).forEach(observeRoot);
    }
  };

  const reset = (root) => {
    if (!root) return;
    if (root._zaynDedicatedCounterObserver) {
      root._zaynDedicatedCounterObserver.disconnect();
      root._zaynDedicatedCounterObserver = null;
    }
    root.querySelectorAll(COUNTER_SELECTOR).forEach((element) => {
      if (element._zaynCounterRaf) cancelAnimationFrame(element._zaynCounterRaf);
    });
    delete root.dataset.zaynDedicatedCounterBound;
    delete root.dataset.zaynDedicatedCounterDone;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  window.addEventListener('pageshow', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches && event.target.matches(ROOT_SELECTOR)) reset(event.target);
    if (event.target.querySelectorAll) {
      event.target.querySelectorAll(ROOT_SELECTOR).forEach(reset);
    }
  });
})();

(() => {
  const PRODUCT_MAP = {
    '4477097321': {
      handle: 'womens-leaf-print-a-line-mini-dress-with-pockets-sleeveless-summer-vacation-outfit',
      title: 'Leaf Print A-Line Mini Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/301c987c-il_fullxfull.8103961434_2p61.jpg?v=1783201211'
    },
    '4526253000': {
      handle: 'amalfi-lemon-tile-midi-dress-sleeveless-cotton-slub-a-line-vacation-resort',
      title: 'Amalfi Lemon Tile Cotton Midi Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/6def78bc-il_fullxfull.8174743744_draq.jpg?v=1783201180'
    },
    '4521232678': {
      handle: 'abstract-print-a-line-mini-dress-with-pockets-retro-resort-summer-style',
      title: 'Abstract Print A-Line Mini Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/ChatGPT_Image_10_Agu_2026_12_29_25_5.png?v=1786354533'
    },
    '4507100658': {
      handle: 'retro-floral-v-neck-tank-top-70s-boho-summer-sleeveless-blouse',
      title: 'Retro Floral V-Neck Tank Top',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/42785d8b-il_fullxfull.8222706943_po7s.jpg?v=1783201191'
    },
    '4525101985': {
      handle: 'whale-shark-kaftan-dress-coastal-v-neck-maxi-nautical-resort-print-loose-fit-beach-style',
      title: 'Whale Shark Kaftan Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/d5f5387f-il_fullxfull.8159428756_lurf.jpg?v=1783201171'
    },
    '4365321154': {
      handle: 'one-piece-swimsuit-women-abstract-modern-swimwear-artistic-summer-beach-bodysuit',
      title: 'Abstract One-Piece Swimsuit',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/daf36261-il_fullxfull.7361913953_3xif.jpg?v=1783201163'
    },
    '4349519312': {
      handle: 'floral-sleeveless-tank-top-lightweight-summer-vacation-blouse-relaxed-fit-casual-top',
      title: 'Floral Sleeveless Tank Top',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/9e01b4df-il_fullxfull.8049810990_sqec.jpg?v=1783201200'
    },
    '4503244729': {
      handle: 'whale-print-maxi-dress-coastal-marine-art-resort-wear',
      title: 'Whale Print Maxi Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/7fcd7886-il_fullxfull.8010183024_q3yg.jpg?v=1783201151'
    },
    '4507107895': {
      handle: 'cream-cotton-whale-shark-t-shirt-coastal-ocean-graphic-tee',
      title: 'Whale Shark Cotton T-Shirt',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/2bd27ff2-il_fullxfull.8044077972_39yv.jpg?v=1783201200'
    },
    '4483876984': {
      handle: 'whale-shark-print-cotton-a-line-dress-with-pockets-beach-resort-summer-wear',
      title: 'Whale Shark A-Line Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/e2b6a475-il_fullxfull.7883298130_gnuu.jpg?v=1783201199'
    },
    '4487322956': {
      handle: 'retro-wave-a-line-pocket-dress-colorful-statement-summer-dress',
      title: 'Retro Wave Dress with Pockets',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/ChatGPT_Image_10_Agu_2026_14_08_36_4.png?v=1786360149'
    },
    '4446223307': {
      handle: 'retro-floral-a-line-mini-dress-sleeveless-vacation-summer-outfit-with-pockets',
      title: 'Retro Floral A-Line Mini Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/dd331187-il_fullxfull.8022627414_ru4o.jpg?v=1783201248'
    },
    '4464577070': {
      handle: 'indigo-fish-print-a-line-pocket-dress-sleeveless-summer-beach-vacation',
      title: 'Indigo Fish A-Line Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/c900267e-il_fullxfull.7804733435_skb7.jpg?v=1783201159'
    },
    '4523032903': {
      handle: 'celestial-floral-mini-dress-navy-moon-stars-spaghetti-strap-summer-dress',
      title: 'Celestial Floral Mini Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/b724b8f6-il_fullxfull.8237247713_3w09.jpg?v=1783201167'
    },
    '4486485553': {
      handle: 'whale-shark-print-tunic-dress-ocean-inspired-v-neck-summer-beachwear',
      title: 'Whale Shark Tunic Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/615b7c7a-il_fullxfull.8222727459_q3m1.jpg?v=1783201188'
    },
    '4446216757': {
      handle: 'retro-floral-maxi-dress-boho-sleeveless-summer-style',
      title: 'Retro Floral Maxi Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/888c6b22-il_fullxfull.7634780834_cwmq.jpg'
    },
    '4515395647': {
      handle: 'lemon-watercolor-a-line-dress-sleeveless-mediterranean-resort-wear',
      title: 'Lemon Watercolor A-Line Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/il_fullxfull.8279100675_s9yf.jpg?v=1784991527'
    },
    '4488120524': {
      handle: 'fish-print-a-line-pocket-dress-coastal-vacation-dress-mediterranean-resort-wear',
      title: 'Fish Print A-Line Pocket Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/867a2784-il_fullxfull.8224711485_n8cq.jpg?v=1783201196'
    },
    '4506114205': {
      handle: 'mediterranean-fish-print-dress-coastal-resort-mini-dress-retro-summer-outfit-women',
      title: 'Mediterranean Fish Print A-Line Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/ChatGPT_Image_10_Agu_2026_12_29_25_4.png?v=1786355246'
    },
    '4501949055': {
      handle: 'retro-wave-cotton-resort-shirt-70s-artistic-vacation-menswear-mediterranean-summer-style',
      title: 'Men’s Retro Wave Cotton Resort Shirt',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/b777cbe7-il_fullxfull.8225849803_i4nw.jpg?v=1783201175'
    },
    '4482739986': {
      handle: 'navy-botanical-a-line-dress-watercolor-leaf-print-mini-sleeveless-summer-flowy',
      title: 'Navy Botanical A-Line Mini Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/69dcf88d-il_fullxfull.7876972940_oqh9.jpg?v=1783201239'
    },
    '4511070645': {
      handle: 'coastal-fish-mini-dress-mediterranean-vacation-dress-with-relaxed-boutique-energy',
      title: 'Coastal Fish Mini Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/a1d32a91-il_fullxfull.8192339757_j68w.jpg?v=1783201195'
    },
    '4510134464': {
      handle: 'retro-a-line-midi-dress-earth-tone-wave-pattern-pockets-vintage-inspired-silhouette',
      title: 'Retro Wave A-Line Midi Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/4d663483-il_fullxfull.8174788068_t7vb.jpg?v=1783201192'
    },
    '4334757466': {
      handle: '60s-retro-black-floral-a-line-mini-dress-with-pockets-mod-event-outfit',
      title: 'Black Floral A-Line Mini Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/f535854b-il_fullxfull.7869804679_1xw4.jpg?v=1783201223'
    },
    '4477149312': {
      handle: 'floral-a-line-mini-dress-with-pockets-european-summer-vacation-dress',
      title: 'Floral A-Line Mini Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/758c3a17-il_fullxfull.8177784273_6m8i.jpg?v=1783201211'
    },
    '4467177283': {
      handle: 'whale-shark-maxi-dress-ocean-print-vacation-dress-coastal-resort-summer-dress',
      title: 'Whale Shark Maxi Dress',
      image: 'https://cdn.shopify.com/s/files/1/0802/3162/8985/files/7b1d83aa-il_fullxfull.7822067943_9klh.jpg?v=1783201163'
    }
  };

  const ROOT_SELECTOR = '.zayn-proof-gallery';
  const SOURCE_SELECTOR = '.zayn-proof-gallery__review-source-link[href*="/listing/"]';
  const TILE_CLASS = 'zayn-proof-gallery__purchased-product';

  const thumbnailUrl = (source) => {
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', '120');
      return url.toString();
    } catch (_) {
      return source;
    }
  };

  const injectStyles = () => {
    if (document.getElementById('ZaynPurchasedProductStyles')) return;
    const style = document.createElement('style');
    style.id = 'ZaynPurchasedProductStyles';
    style.textContent = `
      .${TILE_CLASS} {
        display: grid;
        grid-template-columns: 56px minmax(0, 1fr) auto;
        gap: 11px;
        align-items: center;
        width: 100%;
        margin-top: 17px;
        padding: 8px 10px 8px 8px;
        border: 1px solid rgb(23 49 44 / 17%);
        border-radius: 10px;
        color: inherit;
        background: rgb(255 255 255 / 42%);
        font-family: var(--font-body--family, sans-serif);
        text-decoration: none;
        transition: border-color 180ms ease, background-color 180ms ease, transform 180ms ease;
      }
      .${TILE_CLASS}:hover {
        border-color: rgb(23 49 44 / 34%);
        background: rgb(255 255 255 / 68%);
        transform: translateY(-1px);
      }
      .${TILE_CLASS}:focus-visible {
        outline: 2px solid var(--proof-accent, currentColor);
        outline-offset: 2px;
      }
      .${TILE_CLASS}__image {
        display: block;
        width: 56px;
        height: 56px;
        border-radius: 7px;
        object-fit: cover;
        background: rgb(23 49 44 / 6%);
      }
      .${TILE_CLASS}__copy {
        display: grid;
        gap: 4px;
        min-width: 0;
      }
      .${TILE_CLASS}__eyebrow {
        color: rgb(23 49 44 / 56%);
        font-size: .52rem;
        font-weight: 850;
        letter-spacing: .11em;
        line-height: 1.15;
        text-transform: uppercase;
      }
      .${TILE_CLASS}__title {
        display: -webkit-box;
        overflow: hidden;
        color: var(--proof-ink, currentColor);
        font-size: .72rem;
        font-weight: 750;
        line-height: 1.25;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
      }
      .${TILE_CLASS}__arrow {
        color: var(--proof-ink, currentColor);
        font-size: .95rem;
        line-height: 1;
      }
      .${TILE_CLASS} + .zayn-proof-gallery__review-source-link {
        margin-top: 12px;
      }
      @media (max-width: 749px) {
        .${TILE_CLASS} {
          grid-template-columns: 52px minmax(0, 1fr) auto;
          gap: 10px;
        }
        .${TILE_CLASS}__image {
          width: 52px;
          height: 52px;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .${TILE_CLASS} { transition: none; }
      }
    `;
    document.head.appendChild(style);
  };

  const createTile = (product) => {
    const link = document.createElement('a');
    link.className = TILE_CLASS;
    link.href = `/products/${product.handle}`;
    link.setAttribute('aria-label', `View ${product.title}`);

    const image = document.createElement('img');
    image.className = `${TILE_CLASS}__image`;
    image.src = thumbnailUrl(product.image);
    image.alt = product.title;
    image.width = 56;
    image.height = 56;
    image.loading = 'lazy';
    image.decoding = 'async';

    const copy = document.createElement('span');
    copy.className = `${TILE_CLASS}__copy`;

    const eyebrow = document.createElement('span');
    eyebrow.className = `${TILE_CLASS}__eyebrow`;
    eyebrow.textContent = 'Purchased item';

    const title = document.createElement('span');
    title.className = `${TILE_CLASS}__title`;
    title.textContent = product.title;

    const arrow = document.createElement('span');
    arrow.className = `${TILE_CLASS}__arrow`;
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';

    copy.append(eyebrow, title);
    link.append(image, copy, arrow);
    return link;
  };

  const enhance = (scope = document) => {
    injectStyles();
    const roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      root.querySelectorAll(SOURCE_SELECTOR).forEach((sourceLink) => {
        const match = sourceLink.href.match(/\/listing\/(\d+)/);
        if (!match) return;

        const product = PRODUCT_MAP[match[1]];
        if (!product) return;

        const card = sourceLink.closest('[data-review-card]');
        if (!card || card.querySelector(`.${TILE_CLASS}`)) return;

        const footer = card.querySelector('.zayn-proof-gallery__review-footer') || sourceLink.parentElement;
        if (!footer) return;

        const tile = createTile(product);
        footer.insertBefore(tile, sourceLink);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhance(), { once: true });
  } else {
    enhance();
  }

  window.addEventListener('pageshow', () => enhance());
  document.addEventListener('shopify:section:load', (event) => enhance(event.target));
})();
